import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import YooptaEditor, { createYooptaEditor, YooptaContentValue } from '@yoopta/editor';
import { Bold, Italic, CodeMark, Underline, Strike, Highlight } from '@yoopta/marks';

// Import Tools
import ActionMenu, { DefaultActionMenuRender } from '@yoopta/action-menu-list';
import Toolbar, { DefaultToolbarRender } from '@yoopta/toolbar';
import LinkTool, { DefaultLinkToolRender } from '@yoopta/link-tool';

import { convertYooptaToMarkdown, convertMarkdownToYoopta, getConfiguredPlugins } from '../../utils/yooptaConverter';

interface YooptaMarkdownEditorProps {
  /**
   * Initial Yoopta content value (pre-converted from markdown) OR raw markdown content
   */
  initialValue?: YooptaContentValue;
  /**
   * Raw markdown content (will be converted to Yoopta if initialValue is not provided)
   */
  markdownContent?: string;
  /**
   * Callback fired when the content changes (returns markdown)
   */
  onChange: (markdown: string) => void;
  /**
   * Placeholder text to display when editor is empty
   */
  placeholder?: string;
  /**
   * Whether the editor is in read-only mode
   */
  readOnly?: boolean;
  /**
   * Additional CSS classes to apply to the editor container
   */
  className?: string;
  /**
   * Whether to automatically focus the editor on mount
   */
  autoFocus?: boolean;
  /**
   * User ID for media uploads
   */
  userId?: string;
  /**
   * Bucket name for image uploads (default: 'proposalimages')
   */
  bucketName?: string;
}

// Tools configuration - defined outside component for stability
const TOOLS = {
  ActionMenu: {
    tool: ActionMenu,
    render: DefaultActionMenuRender,
  },
  Toolbar: {
    tool: Toolbar,
    render: DefaultToolbarRender,
  },
  LinkTool: {
    tool: LinkTool,
    render: DefaultLinkToolRender,
  },
};

/**
 * Create a safe default Yoopta value
 */
function createSafeYooptaValue(content?: string): YooptaContentValue {
  // Use the same approach as the converter utility
  return convertMarkdownToYoopta(content || 'Start editing...');
}

/**
 * BULLETPROOF YOOPTA EDITOR - Handles all edge cases with improved state management
 */
const YooptaMarkdownEditor: React.FC<YooptaMarkdownEditorProps> = ({
  initialValue,
  markdownContent,
  onChange,
  placeholder = "Start typing...",
  readOnly = false,
  className = "",
  autoFocus = false,
  userId,
  bucketName = "proposalimages"
}) => {
  // Removed excessive component render logging
  // Get configured plugins with media upload support
  const configuredPlugins = useMemo(() => {
    const plugins = getConfiguredPlugins(userId, bucketName);
    // Plugins configured silently
    return plugins;
  }, [userId, bucketName]);

  // Create stable editor instance
  const editor = useMemo(() => {
    const editorInstance = createYooptaEditor();
    // Editor instance created silently
    return editorInstance;
  }, []);

  // Stable marks configuration
  const marks = useMemo(() => [
    Bold,
    Italic,
    CodeMark,
    Underline,
    Strike,
    Highlight
  ], []);

  // Track if we're in the middle of an update to prevent loops
  const isUpdatingRef = useRef(false);
  
  // Track the last markdown content to detect changes
  const lastMarkdownContentRef = useRef<string | undefined>(markdownContent);

  // Create initial value with improved logic - memoized to prevent recreation
  const getInitialValue = useMemo((): YooptaContentValue => {
    console.log('🔄 YooptaEditor: Determining initial value...', {
      hasInitialValue: !!initialValue,
      hasMarkdownContent: !!markdownContent,
      initialValueKeys: initialValue ? Object.keys(initialValue) : [],
      markdownLength: markdownContent?.length || 0,
      lastMarkdownContent: lastMarkdownContentRef.current
    });

    // Priority 1: Use provided initialValue if valid
    if (initialValue && typeof initialValue === 'object' && Object.keys(initialValue).length > 0) {
      console.log('✅ Using provided initialValue');
      // Validate the structure to prevent "elements" errors
      const firstKey = Object.keys(initialValue)[0];
      const firstBlock = initialValue[firstKey];
      if (firstBlock && typeof firstBlock === 'object') {
        return initialValue;
      } else {
        console.warn('⚠️ Invalid initialValue structure, creating fallback');
      }
    }

    // Priority 2: Convert markdownContent to Yoopta
    if (markdownContent !== undefined) { // Allow empty string
      console.log('🔄 Converting markdown to Yoopta...');
      try {
        const converted = convertMarkdownToYoopta(markdownContent);
        console.log('✅ Markdown converted successfully');
        return converted;
      } catch (error) {
        console.error('❌ Failed to convert markdown:', error);
      }
    }

    // Priority 3: Create safe fallback
    console.log('🔄 Creating safe fallback value');
    return createSafeYooptaValue('Start editing...');
  }, [initialValue, markdownContent]); // Only recreate when these props actually change

  // State for editor value with safe initialization
  const [value, setValue] = useState<YooptaContentValue>(() => getInitialValue);



  // Track if user is actively editing to prevent interference
  const [isUserEditing, setIsUserEditing] = useState(false);
  const userEditingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update value when props change - but ONLY if user is not actively editing
  useEffect(() => {
    // Skip if we're in the middle of an update OR user is actively editing
    if (isUpdatingRef.current || isUserEditing) {
      console.log('🔄 Skipping prop update - user is editing or update in progress');
      return;
    }

    // Check if markdown content actually changed
    if (markdownContent !== lastMarkdownContentRef.current) {
      console.log('🔄 YooptaEditor: External markdown content changed, updating value:', {
        oldLength: lastMarkdownContentRef.current?.length || 0,
        newLength: markdownContent?.length || 0,
        source: 'EXTERNAL_PROP_CHANGE'
      });

      const newValue = getInitialValue;
      setValue(newValue);
      lastMarkdownContentRef.current = markdownContent;
    }
  }, [markdownContent, getInitialValue, isUserEditing]);

  // Handle editor changes
  const handleEditorChange = useCallback((newValue: YooptaContentValue) => {
    console.log('🔥 YooptaEditor handleEditorChange CALLED:', {
      blockCount: Object.keys(newValue).length,
      hasBlocks: Object.keys(newValue).length > 0,
      isUpdating: isUpdatingRef.current,
      firstBlockSample: Object.keys(newValue).length > 0 ? {
        id: Object.keys(newValue)[0],
        type: newValue[Object.keys(newValue)[0]]?.type,
        elements: newValue[Object.keys(newValue)[0]]?.elements?.length || 0,
        elementSample: newValue[Object.keys(newValue)[0]]?.elements?.[0]
      } : null
    });

    // Mark user as actively editing
    setIsUserEditing(true);
    
    // Clear any existing timeout
    if (userEditingTimeoutRef.current) {
      clearTimeout(userEditingTimeoutRef.current);
    }
    
    // Set timeout to clear editing state after user stops typing
    userEditingTimeoutRef.current = setTimeout(() => {
      setIsUserEditing(false);
      console.log('🔄 User editing session ended');
    }, 1000); // 1 second after last change

    // Set updating flag to prevent loops
    isUpdatingRef.current = true;

    // Always update local state first
    setValue(newValue);

    try {
      // Convert to markdown and notify parent
      const markdownContent = convertYooptaToMarkdown(newValue);
      
      console.log('🔥 YooptaEditor ABOUT TO CALL onChange:', {
        contentLength: markdownContent.length,
        hasContent: !!markdownContent.trim(),
        preview: markdownContent.substring(0, 100) + '...',
        willCallOnChange: true
      });

      // Don't send empty or error content - wait for real user input
      if (!markdownContent || 
          markdownContent.trim() === '' ||
          markdownContent === 'Content loading...' ||
          markdownContent === 'Error loading content...') {
        console.log('⏭️ Skipping empty/error content, waiting for real user input');
        return;
      }

      // Update the last markdown content ref
      lastMarkdownContentRef.current = markdownContent;

      // Always notify parent - let parent decide how to handle content
      console.log('🚨 CALLING onChange NOW with content length:', markdownContent.length);
      onChange(markdownContent);
      console.log('✅ onChange CALLED SUCCESSFULLY');
    } catch (error) {
      console.error('❌ Failed to convert Yoopta to markdown:', error);
      // Don't call onChange with empty string - keep the previous content
    } finally {
      // Keep updating flag set for longer to prevent useEffect interference
      setTimeout(() => {
        isUpdatingRef.current = false;
        console.log('🔄 isUpdatingRef cleared after delay');
      }, 200); // Increased delay to prevent re-entry
    }
  }, [onChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (userEditingTimeoutRef.current) {
        clearTimeout(userEditingTimeoutRef.current);
      }
    };
  }, []);

  // One-time test of editor functionality (only on mount)
  useEffect(() => {
    console.log('🚨 YooptaEditor mounted with:', {
      editorInstance: !!editor,
      valueKeys: value ? Object.keys(value).length : 0
    });
  }, []); // Empty dependency - only run once on mount

  // Validate value before rendering
  if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
    const fallbackValue = createSafeYooptaValue('Loading content...');
    if (JSON.stringify(value) !== JSON.stringify(fallbackValue)) {
      setValue(fallbackValue);
    }
    return (
      <div className={`min-h-[400px] w-full border border-gray-300 rounded-lg bg-white ${className}`}>
        <div className="flex items-center justify-center h-[400px] text-gray-500">
          <div className="text-center">
            <div className="text-lg mb-2">⏳</div>
            <div>Preparing editor...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-[400px] w-full border border-gray-300 rounded-lg bg-white ${className}`}>
      {/* Header */}
      <div className="border-b border-gray-200 p-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-gray-700">
            Rich Text Editor
          </div>
          <div className="text-xs text-gray-500">
            {value && typeof value === 'object' ? Object.keys(value).length : 0} block{(value && typeof value === 'object' ? Object.keys(value).length : 0) !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Editor Content */}
      <div className="p-4 w-full h-full overflow-auto">
        <div className="w-full h-full min-h-[500px]">
          {(() => {
            const hasValidValue = value && typeof value === 'object' && Object.keys(value).length > 0;
            // Render check passed - showing editor
            return hasValidValue;
          })() ? (
            <>
              <div 
                onFocus={() => {
                  console.log('🚨 EDITOR FOCUSED - User started editing');
                  setIsUserEditing(true);
                }}
                onBlur={() => {
                  console.log('🚨 EDITOR BLURRED - User may have stopped editing');
                  // Don't immediately set to false, let the timeout handle it
                }}
              >
                <YooptaEditor
                  editor={editor}
                  plugins={configuredPlugins}
                  marks={marks}
                  tools={TOOLS}
                  value={value}
                  onChange={handleEditorChange}
                  placeholder={placeholder}
                  readOnly={readOnly}
                  autoFocus={autoFocus}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '500px',
                    maxHeight: 'calc(100vh - 200px)',
                    overflow: 'auto',
                    border: 'none'
                  }}
                />
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              <div className="text-center">
                <div className="text-lg mb-2">⏳</div>
                <div>Loading editor...</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default YooptaMarkdownEditor;